import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
    Clock, FileText, Image as ImageIcon,
    CheckCircle2, AlertCircle, RefreshCw
} from 'lucide-react';
import type { HistoricalEventType } from './types';

interface AdminReferenceEventTableProps {
    eventTypeData: any[];
    totalHistoricalEvents: number;
    eventsWithPrompts: number;
    eventsWithImages: number;
    eventPromptsProgress: number;
    eventImagesProgress: number;
    generatingEventTypes: Set<HistoricalEventType>;
    regeneratingEventTypes: Set<HistoricalEventType>;
    onGenerateEventType: (type: HistoricalEventType, missing: string[]) => void;
    onRegenerateEventType: (type: HistoricalEventType) => void;
    onBatchGeneratePrompts: () => void;
    onBatchGenerateImages: () => void;
}

export const AdminReferenceEventTable = React.memo(({
    eventTypeData,
    totalHistoricalEvents,
    eventsWithPrompts,
    eventsWithImages,
    eventPromptsProgress,
    eventImagesProgress,
    generatingEventTypes,
    regeneratingEventTypes,
    onGenerateEventType,
    onRegenerateEventType,
    onBatchGeneratePrompts,
    onBatchGenerateImages
}: AdminReferenceEventTableProps) => {
    return (
        <Card className="border-l-4 border-l-purple-600">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Historical Events by Type
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                {/* Image Generation Stats */}
                <div className="grid grid-cols-2 gap-3 p-3 bg-muted/30 rounded-lg border">
                    <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">Image Prompts</span>
                            <span className="font-medium">{eventsWithPrompts} / {totalHistoricalEvents}</span>
                        </div>
                        <Progress value={eventPromptsProgress} className="h-1.5" />
                    </div>
                    <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">Images Generated</span>
                            <span className="font-medium">{eventsWithImages} / {totalHistoricalEvents}</span>
                        </div>
                        <Progress value={eventImagesProgress} className="h-1.5" />
                    </div>
                </div>

                {/* Batch Image Operations */}
                <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-900">
                    <div className="flex-1 text-xs text-muted-foreground">
                        Generate image prompts and images for all historical events
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            size="sm"
                            onClick={onBatchGeneratePrompts}
                            variant="outline"
                            className="text-xs h-8"
                        >
                            <FileText className="h-3 w-3 mr-1" />
                            Generate Missing Prompts ({totalHistoricalEvents - eventsWithPrompts})
                        </Button>
                        <Button
                            size="sm"
                            onClick={onBatchGenerateImages}
                            variant="outline"
                            className="text-xs h-8"
                        >
                            <ImageIcon className="h-3 w-3 mr-1" />
                            Generate Missing Images ({eventsWithPrompts - eventsWithImages})
                        </Button>
                    </div>
                </div>

                {eventTypeData.map((eventType) => {
                    const isGenerating = generatingEventTypes.has(eventType.type);
                    const isRegenerating = regeneratingEventTypes.has(eventType.type);
                    const progress = eventType.total > 0 ? (eventType.existing / eventType.total) * 100 : 0;
                    const isComplete = eventType.existing === eventType.total;
                    const hasMissing = eventType.missing.length > 0;
                    const hasExisting = eventType.existing > 0;

                    return (
                        <div key={eventType.type} className="flex items-center gap-2 p-3 border rounded-lg">
                            <div className="flex-1">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm">{eventType.label}</span>
                                        <span className="text-xs text-muted-foreground">
                                            ({eventType.existing} / {eventType.total})
                                        </span>
                                    </div>
                                    {isComplete ? (
                                        <Badge variant="default" className="bg-green-600 text-xs">
                                            <CheckCircle2 className="h-3 w-3 mr-1" />
                                            Complete
                                        </Badge>
                                    ) : hasMissing ? (
                                        <Badge variant="secondary" className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100 text-xs">
                                            <AlertCircle className="h-3 w-3 mr-1" />
                                            {eventType.missing.length} Missing
                                        </Badge>
                                    ) : null}
                                </div>
                                <Progress value={progress} className="h-1.5" />
                            </div>

                            <div className="flex items-center gap-2">
                                {hasMissing && (
                                    <Button
                                        size="sm"
                                        onClick={() => onGenerateEventType(eventType.type, eventType.missing)}
                                        disabled={isGenerating || isRegenerating}
                                        variant={eventType.existing === 0 ? "default" : "outline"}
                                        className="text-xs h-8"
                                    >
                                        {isGenerating ? (
                                            <>
                                                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                                Generating...
                                            </>
                                        ) : (
                                            <>
                                                <Clock className="h-3 w-3 mr-1" />
                                                Generate ({eventType.missing.length})
                                            </>
                                        )}
                                    </Button>
                                )}

                                {hasExisting && (
                                    <Button
                                        size="sm"
                                        onClick={() => onRegenerateEventType(eventType.type)}
                                        disabled={isGenerating || isRegenerating}
                                        variant="ghost"
                                        className="text-xs h-8"
                                    >
                                        {isRegenerating ? (
                                            <>
                                                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                                Regenerating...
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw className="h-3 w-3 mr-1" />
                                                Regenerate All
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>
                    );
                })}
                <p className="text-xs text-muted-foreground mt-2">
                    Timeline events for historical context (Babylonian Exile, Persian Period, etc.)
                </p>
            </CardContent>
        </Card>
    );
});

AdminReferenceEventTable.displayName = 'AdminReferenceEventTable';
